import React, { useEffect } from "react";
import {
    SortableTable,
    newDisplayCond,
} from "../../components/table/sortableTable";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import dayjs from "dayjs";
import dayjstimezone from "dayjs/plugin/timezone";
import dayjsutc from "dayjs/plugin/utc"; // dependent on utc plugin
import M from "materialize-css";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { OfficePullDown } from "../../components/officePullDown";
import {
    LoadingTableElement,
    NullTableElement,
} from "../../components/table/nullTable";
import * as service from "../../services/usage_based_summary/select";
import { commonSlice, commonState } from "../../store";
import { colors, fontSizes } from "../../theme";
import { JST, numericMonth2view } from "../../utils/time";
import { Row, headCells } from "./columns_billingByOffice";
import { State, paymentsSlice } from "./store";

const uuid = require("uuid");

dayjs.extend(dayjsutc);
dayjs.extend(dayjstimezone);

interface LinkProps {
    month: string;
    office_id: number;
}

const LinkToNext: React.FC<LinkProps> = (props: LinkProps) => {
    const onClick = async () => {
        await service.select_summary_row(props.month, props.office_id);
        service.selectPaymentsByDriver();
    };

    return (
        <MenuItem
            component={Link}
            to="/driver_paid_trips"
            sx={{ color: colors.blue.main }}
            onClick={onClick}
        >
            乗務員別件数参照
        </MenuItem>
    );
};

interface Props {
    state: State;
    commonState: commonState;
}

export const BillingSummaryByOfficeContainer: React.FC<Props> = (
    props: Props
) => {
    const dispatch = useDispatch();

    const setStartMonth = (e: SelectChangeEvent<any>) => {
        const input = props.state.paymentsSummaryInput;
        dispatch(
            paymentsSlice.actions.update_payments_summary_input({
                start_month: e.target.value,
                end_month: input.end_month,
            })
        );
    };

    const setEndMonth = (e: SelectChangeEvent<any>) => {
        const input = props.state.paymentsSummaryInput;
        dispatch(
            paymentsSlice.actions.update_payments_summary_input({
                start_month: input.start_month,
                end_month: e.target.value,
            })
        );
    };
    const clickSearch = () => {
        service.selectPaymentsSummary();
    };
    const start_month_option: JSX.Element[] = [];
    const end_month_option: JSX.Element[] = [];
    for (let i = 0; i < 12; i++) {
        const now = dayjs().tz(JST);
        const month = now.add(-1 * i, "month").format("YYYY/MM");
        start_month_option.push(
            <MenuItem
                key={month}
                selected={props.state.paymentsSummaryInput.start_month == month}
                value={month}
            >
                {month}
            </MenuItem>
        );
        end_month_option.push(
            <MenuItem
                key={month}
                selected={props.state.paymentsSummaryInput.end_month == month}
                value={month}
            >
                {month}
            </MenuItem>
        );
    }
    const selectOffice = (officeId: number): void => {
        dispatch(commonSlice.actions.updateSelectedOffice(officeId));
    };
    useEffect(() => {
        service.selectPaymentsSummary();
        M.FormSelect.init(document.querySelectorAll("select.month_input"));
    }, []);

    return (
        <Box id="Payments-Summary-Screen">
            <Box sx={{ display: "flex", alignItems: "end", m: 3 }}>
                <OfficePullDown
                    selectedOfficeId={props.commonState.selectedOfficeId}
                    onChange={selectOffice}
                    useAllOfficeIds={true}
                />

                <Box sx={{ p: 2 }}>
                    <Box style={{ fontSize: fontSizes.large }}>年月</Box>
                </Box>

                <FormControl
                    variant="standard"
                    sx={{ m: 1, p: 2, minWidth: 120 }}
                >
                    <Select
                        labelId="demo-simple-select-standard-label"
                        id="demo-simple-select-standard"
                        defaultValue={
                            props.state.paymentsSummaryInput.start_month
                        }
                        onChange={setStartMonth}
                        label="month"
                    >
                        {start_month_option}
                    </Select>
                </FormControl>

                <Box sx={{ p: 2 }}>〜</Box>

                <FormControl
                    variant="standard"
                    sx={{ m: 1, p: 2, minWidth: 120 }}
                >
                    <Select
                        labelId="demo-simple-select-standard-label"
                        id="demo-simple-select-standard"
                        defaultValue={
                            props.state.paymentsSummaryInput.end_month
                        }
                        onChange={setEndMonth}
                        label="month"
                    >
                        {end_month_option}
                    </Select>
                </FormControl>

                <Button
                    variant="contained"
                    onClick={clickSearch}
                    color="primary"
                    size="small"
                    sx={{
                        m: 3,
                    }}
                >
                    検索
                </Button>
            </Box>
            <Box>
                <BillingSummaryByOfficeTable {...props} />
            </Box>
        </Box>
    );
};

export const BillingSummaryByOfficeTable: React.FC<Props> = (props: Props) => {
    let _data: Row[] = [];
    if (!props.state.paymentsSummary) {
        return <LoadingTableElement />;
    }

    if (props.state.paymentsSummary.payments) {
        if (props.state.paymentsSummary.payments.length == 0) {
            return <NullTableElement />;
        }

        _data = props.state.paymentsSummary.payments.map(
            (rec): Row => {
                return {
                    year_month: rec.month,
                    payment_amount: rec.price,
                    office_name: rec.officeName,
                    nr_chargeable_drivers: rec.nDrivers,
                    nr_chargeable_trips: rec.nChargeableTrips,
                    fixed: rec.fixed,
                    row_id: uuid.v4(),
                    link_driver_page: (
                        <LinkToNext
                            month={numericMonth2view(rec.month)}
                            office_id={rec.officeId}
                        />
                    ),
                };
            }
        );

        console.debug("BillingSummaryByOfficeTable data: ", _data);
        return (
            <Box>
                <SortableTable
                    tableHeaders={headCells}
                    tableData={_data}
                    tableProps={props}
                    displayCond={newDisplayCond("driver_name", "asc", 0, 20)}
                    rowIdField={"row_id"}
                    onRowClickMethod={() => {}}
                    slimRows={true}
                />
            </Box>
        );
    }
    return <NullTableElement />;
};
